function getSectionSortKey(section) {
  const detail = section?.detail || {};
  const sectionContent = section?.version_details?.version_content || {};
  const raw =
    section?.sibling_order ??
    detail.sibling_order ??
    section?.procedure_tree?.sibling_order ??
    sectionContent.sibling_order;
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? 0 : n;
}

function getSectionStableId(section) {
  return (
    section?.tree_node_id ||
    section?.detail?.section_tree_node_id ||
    section?.version_details?.version_content?.section_tree_node_id ||
    section?.section_tree_node_id ||
    section?.section_id_ref ||
    ""
  );
}

/**
 * @returns {number | null}
 * Only treats **whole** labels like `Section 1` as ordinals. Prose titles such as
 * `section 3 for index updated again` must not parse as 3 — that broke sort order vs
 * `sibling_order` (e.g. pasted row `0` should stay above `1`).
 */
export function parseSectionOrdinalFromSectionName(section) {
  if (!section) return null;
  const detail = section.detail || {};
  const sectionContent = section?.version_details?.version_content || {};
  const names = [
    detail.section_name,
    sectionContent.section_name,
    section.section_name,
  ].filter(Boolean);
  for (const name of names) {
    if (typeof name !== "string") continue;
    const t = name.trim();
    const m = t.match(/^Section\s+(\d+)\s*$/i);
    if (m) return parseInt(m[1], 10);
  }
  return null;
}

/**
 * "User Copied Section 1 from …" / "User Moved Section 2 …" — subject section number for preview chrome.
 * Aligns AppBar with audit text when the pasted section sits at a different list index (same-doc copy).
 */
export function parseUserCopiedMovedSectionOrdinal(actionMsg) {
  if (!actionMsg || typeof actionMsg !== "string") return null;
  let m = actionMsg.match(/\bUser\s+(?:Copied|Moved)\s+Section\s+(\d+)\b/i);
  if (!m) m = actionMsg.match(/\b(?:Copied|Moved)\s+Section\s+(\d+)\b/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) && n >= 1 ? n : null;
}

/** "Section 1" / "… Section 2 …" from audit-derived labels (not whole-string anchored). */
export function parseAuditSectionNumberFromLabel(label) {
  if (typeof label !== "string" || !label.trim()) return null;
  const m = label.match(/Section\s+(\d+)/i);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isFinite(n) && n >= 1 ? n : null;
}

/**
 * When every section row has a parseable "Section N" in name and N values are unique,
 * use those numbers (semantic order). Otherwise sibling_order rank can mislabel (e.g. API order
 * sibling_order 2,3 with names "Section 2" and "Section 1").
 */
/**
 * Section index the audit claims (e.g. "Edit Section 2 for …", pageTitle "Section 2",
 * breadcrumb "… > Section 2"). Used to pick the matching snapshot row when tree_node_id
 * points at a different section row.
 */
export function parseTargetSectionOrdinalFromAuditTrail(meta) {
  if (!meta || typeof meta !== "object") return null;
  const actionMsg = [meta.action_to_show, meta.action]
    .filter(Boolean)
    .join(" ")
    .trim();
  const mEdit = actionMsg.match(
    /\b(?:Edit|Save|Update|Create|Updated|User\s+Edited)\s+Section\s+(\d+)\b/i,
  );
  if (mEdit) return parseInt(mEdit[1], 10);
  const mUserSec = actionMsg.match(
    /\bUser\s+(?:Moved|Copied|Deleted)\s+Section\s+(\d+)\b/i,
  );
  if (mUserSec) return parseInt(mUserSec[1], 10);
  const mOpSec = actionMsg.match(/\b(?:Copy|Move|Delete)\s+Section\s+(\d+)\b/i);
  if (mOpSec) return parseInt(mOpSec[1], 10);
  const pageTitle = meta.pageTitle;
  if (typeof pageTitle === "string") {
    const mPt = pageTitle.trim().match(/^Section\s+(\d+)\b/i);
    if (mPt) return parseInt(mPt[1], 10);
  }
  const bc = meta.breadcrumb_text;
  if (typeof bc === "string" && bc.trim()) {
    const segments = bc.split(">").map((p) => p.trim()).filter(Boolean);
    for (const seg of segments) {
      const mSeg = seg.match(/^Section\s+(\d+)\b/i);
      if (mSeg) return parseInt(mSeg[1], 10);
    }
  }
  if (typeof meta.comment === "string" && meta.comment.trim()) {
    const c = meta.comment.trim();
    const fromUserMoved = parseUserCopiedMovedSectionOrdinal(c);
    if (fromUserMoved != null) return fromUserMoved;
    const lbl = parseAuditSectionCopyMoveLabels(c);
    if (lbl?.sectionLabel) {
      const n = parseAuditSectionNumberFromLabel(lbl.sectionLabel);
      if (n != null) return n;
    }
  }
  return null;
}

/**
 * When a snapshot lists multiple top-level section rows, pick the one whose section_name
 * matches the audit-declared section number. Returns a one-element array or null.
 */
export function pickSectionRowsMatchingAuditDeclaredOrdinal(rows, auditMeta) {
  if (!Array.isArray(rows) || rows.length <= 1) return null;
  const target = parseTargetSectionOrdinalFromAuditTrail(auditMeta);
  if (target == null || Number.isNaN(target) || target < 1) return null;
  const sectionRows = rows.filter((r) => r && r.type === "section");
  if (sectionRows.length === 0) return null;
  const matches = sectionRows.filter(
    (s) => parseSectionOrdinalFromSectionName(s) === target,
  );
  if (matches.length === 1) return matches;
  return null;
}

/**
 * Order top-level section rows without dropping any: by "Section N" in section_name when
 * every row parses to a unique N; otherwise by sibling_order. Keeps full snapshot content.
 */
export function sortSectionsForSnapshotDisplay(sections) {
  if (!Array.isArray(sections) || sections.length <= 1) return sections;
  const parsed = sections.map(parseSectionOrdinalFromSectionName);
  const allNamed =
    parsed.every((n) => n != null && !Number.isNaN(n) && n >= 1) &&
    new Set(parsed).size === parsed.length;
  if (allNamed) {
    return [...sections].sort((a, b) => {
      const na = parseSectionOrdinalFromSectionName(a);
      const nb = parseSectionOrdinalFromSectionName(b);
      return (na ?? 0) - (nb ?? 0);
    });
  }
  return [...sections].sort((a, b) => {
    const oa = getSectionSortKey(a);
    const ob = getSectionSortKey(b);
    if (oa !== ob) return oa - ob;
    return sections.indexOf(a) - sections.indexOf(b);
  });
}

function allSectionsHaveUniqueParsedOrdinals(allSections) {
  if (!Array.isArray(allSections) || allSections.length <= 1) return false;
  const parsed = allSections.map((s) => parseSectionOrdinalFromSectionName(s));
  if (parsed.some((n) => n == null || Number.isNaN(n) || n < 1)) return false;
  return new Set(parsed).size === parsed.length;
}

/**
 * Human label for "Section N" when the tree is filtered to a subset (e.g. one section for Save Step).
 * Prefer "Section N" parsed from section_name; else 1-based rank among siblings (sorts by sibling_order
 * so 0-based API 0,1,2,3 and 1-based 1,2,3,4 both show Section 1..N correctly); else list index + 1.
 */
export function getSectionDisplayOrdinal(section, index, allSections) {
  const i = typeof index === "number" && !Number.isNaN(index) ? index : 0;
  if (!section) return i + 1;
  const detail = section.detail || {};
  const sectionContent = section?.version_details?.version_content || {};

  const computeSectionRankAmong = () => {
    if (!Array.isArray(allSections) || allSections.length === 0) return -1;
    const sid = getSectionStableId(section);
    const sorted = [...allSections].sort((a, b) => {
      const oa = getSectionSortKey(a);
      const ob = getSectionSortKey(b);
      if (oa !== ob) return oa - ob;
      return allSections.indexOf(a) - allSections.indexOf(b);
    });
    return sorted.findIndex((s) => {
      if (s === section) return true;
      if (sid && getSectionStableId(s) === sid) return true;
      return false;
    });
  };

  /* Multi-section tree with allSections: prefer unique "Section N" from names when unambiguous;
   * else rank by sibling_order (snapshot order). Skipping name-first caused wrong labels when
   * sibling_order sort order did not match semantic section_name (Edit Section audits). */
  if (Array.isArray(allSections) && allSections.length > 1) {
    if (allSectionsHaveUniqueParsedOrdinals(allSections)) {
      const fromName = parseSectionOrdinalFromSectionName(section);
      if (fromName != null) return fromName;
    }
    const rank = computeSectionRankAmong();
    if (rank >= 0) return rank + 1;
  }

  const fromNameSingle = parseSectionOrdinalFromSectionName(section);
  if (fromNameSingle != null) return fromNameSingle;
  if (Array.isArray(allSections) && allSections.length > 0) {
    /**
     * Narrowed audit preview (e.g. Save Step): only one section row is passed but its
     * `sibling_order` is the real index among all sections in the snapshot (e.g. "3" for
     * Section 3). List rank here is always 0 → would wrongly show "Section 1".
     */
    if (allSections.length === 1) {
      const soOnly =
        section.sibling_order ??
        detail.sibling_order ??
        section.procedure_tree?.sibling_order ??
        sectionContent.sibling_order;
      const ordOnly = parseInt(soOnly, 10);
      if (!Number.isNaN(ordOnly) && ordOnly > 0) return ordOnly;
      if (ordOnly === 0) return 1;
    }
    const rank = computeSectionRankAmong();
    if (rank >= 0) return rank + 1;
  }
  const so =
    section.sibling_order ??
    detail.sibling_order ??
    section.procedure_tree?.sibling_order ??
    sectionContent.sibling_order;
  const n = parseInt(so, 10);
  if (!Number.isNaN(n) && n > 0) return n;
  return i + 1;
}

function getStepSortKey(step) {
  const detail = step?.detail || {};
  const vc = step?.version_details?.version_content || {};
  const raw =
    step?.sibling_order ??
    step?.procedure_tree?.sibling_order ??
    detail.sibling_order ??
    vc.sibling_order;
  const n = parseInt(raw, 10);
  return Number.isNaN(n) ? 0 : n;
}

function getStepStableId(step) {
  return (
    step?.tree_node_id ||
    step?.detail?.step_tree_node_id ||
    step?.version_details?.version_content?.step_tree_node_id ||
    step?.step_tree_node_id ||
    step?.step_id_ref ||
    ""
  );
}

/**
 * Step ordinal for "Step N" in preview: prefer sibling rank among steps in the section (handles 0-based sibling_order),
 * else "Step N" from pageTitle/step_title, else the running index from the parent.
 */
/**
 * Parses "User Copied Substep 2A from Step 2 of Section 2 ... into ..."
 * so preview labels match the app bar (source substep name) while tree JSON may show destination sibling_order (e.g. Step 1 in Section 1).
 */
/**
 * Section copy/move/delete audits (e.g. "User Copied Section 3 from …", "Copy Section 3 into …").
 * Used when version_content is sparse and the tree is narrowed to one section (ordinal would otherwise show as 1).
 * @returns {{ sectionLabel: string } | null}
 */
export function parseAuditSectionCopyMoveLabels(actionMessage) {
  if (typeof actionMessage !== "string" || !actionMessage.trim()) return null;
  const msg = actionMessage.trim();
  const userM = msg.match(
    /\bUser\s+(?:Moved|Copied|Deleted)\s+(Section\s+\d+)\b/i,
  );
  if (userM) return { sectionLabel: userM[1] };
  const sysM = msg.match(
    /,\s*(?:Copy|Move|Delete)\s+(Section\s+\d+)\b/i,
  );
  if (sysM) return { sectionLabel: sysM[1] };
  const opM = msg.match(/\b(?:Copy|Move|Delete)\s+(Section\s+\d+)\b/i);
  if (opM) return { sectionLabel: opM[1] };
  return null;
}

export function parseAuditSubstepCopyMoveSourceLabels(actionMessage) {
  if (typeof actionMessage !== "string" || !actionMessage.trim()) return null;
  const msg = actionMessage.trim();
  const subM = msg.match(
    /\bUser\s+(?:Moved|Copied)\s+(Substep\s+[\dA-Za-z]+)\s+from\b/i,
  );
  if (!subM) return null;
  const stepM = msg.match(/from\s+Step\s+(\d+)\s+of/i);
  const secM = msg.match(/from\s+Step\s+\d+\s+of\s+(Section\s+\d+)/i);
  return {
    substepHeading: subM[1],
    sourceStepOrdinal: stepM ? parseInt(stepM[1], 10) : null,
    sourceSectionLabel: secM ? secM[1] : null,
  };
}

/**
 * Save / edit / update substep audits (e.g. "Save Substep 1A", comment "Section 1 > Step 1 > Substep 1A").
 * stepOrdinal is the parent step number (from "1" in "Substep 1A" or from comment path).
 * @returns {{ substepHeading: string, stepOrdinal: number | null, sectionLabel: string | null } | null}
 */
export function parseAuditSubstepSaveEditLabels(actionMessage, comment) {
  const msg = typeof actionMessage === "string" ? actionMessage.trim() : "";
  const cmt = typeof comment === "string" ? comment.trim() : "";

  let substepHeading = null;
  const fromMsg =
    msg.match(
      /\b(?:Save|Update|Edit|Create|Created|User\s+Edited|Updated)\s+(Substep\s+[\dA-Za-z]+)\b/i,
    ) ||
    msg.match(
      /\b(Substep\s+[\dA-Za-z]+)\s+(?:was\s+)?(?:saved|updated|edited|created)\b/i,
    );
  if (fromMsg) substepHeading = fromMsg[1];

  if (!substepHeading && cmt) {
    const pathTail = cmt.match(
      /Section\s+\d+\s*>\s*Step\s+\d+\s*>\s*(Substep\s+[\dA-Za-z]+)/i,
    );
    if (pathTail) substepHeading = pathTail[1];
    else {
      const loose = cmt.match(/\b(Substep\s+[\dA-Za-z]+)\b/);
      if (loose) substepHeading = loose[1];
    }
  }

  if (!substepHeading) return null;

  let stepOrdinal = null;
  const fromHead = substepHeading.match(/^Substep\s+(\d+)/i);
  if (fromHead) {
    const n = parseInt(fromHead[1], 10);
    if (!Number.isNaN(n) && n >= 1) stepOrdinal = n;
  }
  if (stepOrdinal == null && cmt) {
    const sm = cmt.match(/\bSection\s+\d+\s*>\s*Step\s+(\d+)\s*>/i);
    if (sm) {
      const n = parseInt(sm[1], 10);
      if (!Number.isNaN(n) && n >= 1) stepOrdinal = n;
    }
  }

  let sectionLabel = null;
  if (cmt) {
    const pathM = cmt.match(
      /(Section\s+\d+)\s*>\s*Step\s+\d+\s*>\s*Substep\s+[\dA-Za-z]+/i,
    );
    if (pathM) sectionLabel = pathM[1];
    else {
      const secM = cmt.match(/\b(Section\s+\d+)\s*>/i);
      if (secM) sectionLabel = secM[1];
    }
  }

  return { substepHeading, stepOrdinal, sectionLabel };
}

/**
 * Parses save/update/create step audit messages (e.g. "Save Step 1 for …") so preview
 * body labels can match the app bar when snapshot sibling_order differs.
 * @returns {{ stepOrdinal: number, sectionLabel: string | null } | null}
 */
export function parseAuditStepSaveActionLabels(actionMessage, comment) {
  const msg = typeof actionMessage === "string" ? actionMessage.trim() : "";
  const cmt = typeof comment === "string" ? comment.trim() : "";

  let stepOrdinal = null;
  const fromMsg =
    msg.match(/\b(?:Save|Update|Edit|Create|Created)\s+Step\s+(\d+)/i) ||
    msg.match(/\bStep\s+(\d+)\s+(?:was\s+)?(?:saved|updated|edited|created)\b/i) ||
    /* "User Edited Step 1" — \bEdit\s+Step does not match "Edited" */
    msg.match(/\b(?:User\s+)?Edited\s+Step\s+(\d+)/i) ||
    msg.match(/\bUpdated\s+Step\s+(\d+)/i) ||
    msg.match(/\b(?:User\s+)?(?:Moved|Copied|Deleted)\s+Step\s+(\d+)/i);
  if (fromMsg) {
    const n = parseInt(fromMsg[1], 10);
    if (!Number.isNaN(n) && n >= 1) stepOrdinal = n;
  }
  if (stepOrdinal == null && cmt) {
    const fromCmt =
      cmt.match(/(?:^|>)\s*Step\s+(\d+)\s*(?:\n|$|,|\s)/i) ||
      cmt.match(/\bSection\s+\d+\s*>\s*Step\s+(\d+)/i);
    if (fromCmt) {
      const n = parseInt(fromCmt[1], 10);
      if (!Number.isNaN(n) && n >= 1) stepOrdinal = n;
    }
  }
  if (stepOrdinal == null) return null;

  let sectionLabel = null;
  if (cmt) {
    const pathM = cmt.match(/(Section\s+\d+)\s*>\s*Step\s+\d+/i);
    if (pathM) sectionLabel = pathM[1];
    else {
      const secM = cmt.match(/\b(Section\s+\d+)\s*>/i);
      if (secM) sectionLabel = secM[1];
    }
  }

  return { stepOrdinal, sectionLabel };
}

/**
 * 1-based step number across the entire procedure: steps in earlier sections (in
 * {@link sortSectionsForSnapshotDisplay} order) plus rank within this section.
 * Matches snapshot semantics (e.g. Section 3 showing Step 6 and Step 7 when prior sections have five steps).
 *
 * @returns {number | null} null if the step/section cannot be resolved in the tree.
 */
export function getProcedureWideStepDisplayOrdinal(step, section, allSections) {
  if (!step || !Array.isArray(allSections) || allSections.length === 0) {
    return null;
  }
  const sortedSections = sortSectionsForSnapshotDisplay(allSections);
  const targetSecId = getSectionStableId(section);
  let priorStepCount = 0;

  for (const sec of sortedSections) {
    const steps = sec?.steps || [];
    const sortedSteps = [...steps].sort((a, b) => {
      const oa = getStepSortKey(a);
      const ob = getStepSortKey(b);
      if (oa !== ob) return oa - ob;
      return steps.indexOf(a) - steps.indexOf(b);
    });

    const sameSection =
      sec === section ||
      (targetSecId &&
        getSectionStableId(sec) === targetSecId &&
        targetSecId.length > 0);

    if (sameSection) {
      const stepSid = getStepStableId(step);
      const rank = sortedSteps.findIndex((s) => {
        if (s === step) return true;
        if (stepSid && getStepStableId(s) === stepSid) return true;
        return false;
      });
      if (rank >= 0) return priorStepCount + rank + 1;
      return priorStepCount + 1;
    }
    priorStepCount += sortedSteps.length;
  }
  return null;
}

/**
 * @param {boolean} [preferRelativeStepOrdinal] - When true (e.g. /audit-trail/review View Original),
 *   use 1-based rank among visible steps in the section even if there is only one step, so "Step 1"
 *   matches the first row shown under that section. When false (default, e.g. move/copy narrow preview),
 *   a single step still uses sibling_order so "Step 5" stays correct when the snapshot lists one row.
 */
export function getStepDisplayOrdinal(
  step,
  stepIdx,
  stepCountFallback,
  allStepsInSection,
  preferRelativeStepOrdinal = false,
) {
  const idx =
    typeof stepIdx === "number" && !Number.isNaN(stepIdx) ? stepIdx : 0;
  if (!step) {
    return typeof stepCountFallback === "number" && stepCountFallback > 0
      ? stepCountFallback
      : idx + 1;
  }
  /* Rank among visible siblings when multiple steps, or when preferRelativeStepOrdinal (review UI). */
  if (Array.isArray(allStepsInSection) && allStepsInSection.length > 0) {
    const sid = getStepStableId(step);
    const sorted = [...allStepsInSection].sort((a, b) => {
      const oa = getStepSortKey(a);
      const ob = getStepSortKey(b);
      if (oa !== ob) return oa - ob;
      return allStepsInSection.indexOf(a) - allStepsInSection.indexOf(b);
    });
    const rank = sorted.findIndex((s) => {
      if (s === step) return true;
      if (sid && getStepStableId(s) === sid) return true;
      return false;
    });
    if (rank >= 0) {
      const useRank =
        allStepsInSection.length > 1 || preferRelativeStepOrdinal === true;
      if (useRank) return rank + 1;
    }
  }
  const detail = step.detail || {};
  const vc = step.version_details?.version_content || {};
  const raw =
    step.sibling_order ??
    step.procedure_tree?.sibling_order ??
    detail.sibling_order ??
    vc.sibling_order;
  const n = parseInt(raw, 10);
  /* sibling_order from API is 0-based (0 = first step); display is 1-based */
  if (!Number.isNaN(n) && n >= 0) return n + 1;

  const titles = [
    vc.pageTitle,
    detail.pageTitle,
    step.pageTitle,
    vc.step_title,
    detail.step_title,
  ].filter(Boolean);
  for (const t of titles) {
    if (typeof t !== "string") continue;
    const m = t.match(/^Step\s+(\d+)/i);
    if (m) return parseInt(m[1], 10);
  }
  if (typeof stepCountFallback === "number" && stepCountFallback > 0)
    return stepCountFallback;
  return idx + 1;
}

/**
 * Parse app-bar / preview title base (e.g. "Step 1", "Attachment 2", "Rich Text 1") for read-only checkbox emphasis.
 * Strips a trailing parenthetical so "Step 1 (Version …)" still matches.
 */
export function parseReadOnlyCheckboxTitleMatch(titleBase) {
  if (titleBase == null || typeof titleBase !== "string") return null;
  let t = titleBase.trim();
  const paren = t.indexOf("(");
  if (paren >= 0) t = t.slice(0, paren).trim();
  if (!t) return null;
  let m = t.match(/^Step\s+(\d+)\b/i);
  if (m) {
    const n = parseInt(m[1], 10);
    return Number.isNaN(n) ? null : { kind: "step", n };
  }
  m = t.match(/^Section\s+(\d+)\b/i);
  if (m) {
    const n = parseInt(m[1], 10);
    return Number.isNaN(n) ? null : { kind: "section", n };
  }
  m = t.match(/^Substep\s+([\dA-Za-z]+)\b/i);
  if (m) return { kind: "substep", token: String(m[1]).toUpperCase() };
  m = t.match(/^Attachment\s+(\d+)\b/i);
  if (m) {
    const n = parseInt(m[1], 10);
    return Number.isNaN(n) ? null : { kind: "attachment", n };
  }
  m = t.match(/^Rich\s+Text\s+(\d+)\b/i);
  if (m) {
    const n = parseInt(m[1], 10);
    return Number.isNaN(n) ? null : { kind: "richText", n };
  }
  m = t.match(/^Video\s+(\d+)\b/i);
  if (m) {
    const n = parseInt(m[1], 10);
    return Number.isNaN(n) ? null : { kind: "video", n };
  }
  m = t.match(/^Canvas\s+(\d+)\b/i);
  if (m) {
    const n = parseInt(m[1], 10);
    return Number.isNaN(n) ? null : { kind: "canvas", n };
  }
  return null;
}

export function substepHeadingMatchesTitleMatch(heading, match) {
  if (!match || match.kind !== "substep") return false;
  if (!heading || typeof heading !== "string") return false;
  const h = heading.trim().replace(/\s+/g, " ").toUpperCase();
  const expected = `SUBSTEP ${String(match.token).toUpperCase()}`;
  return h === expected;
}

/**
 * Ordinal from snapshot heading (pageTitle / name), e.g. "Attachment 2" for step-local numbering.
 * Procedure-wide getNodeIndices globalIndex can differ — prefer this for display + read-only highlight match.
 * @param {"attachment"|"richText"|"video"|"canvas"} kind
 * @returns {number | null}
 */
export function parseAuditedChildOrdinalFromHeading(heading, kind) {
  if (typeof heading !== "string" || !heading.trim()) return null;
  const t = heading.trim();
  const re =
    kind === "attachment"
      ? /^Attachment\s+(\d+)\b/i
      : kind === "richText"
        ? /^Rich\s+Text\s+(\d+)\b/i
        : kind === "video"
          ? /^Video\s+(\d+)\b/i
          : kind === "canvas"
            ? /^Canvas\s+(\d+)\b/i
            : null;
  if (!re) return null;
  const m = t.match(re);
  if (!m) return null;
  const n = parseInt(m[1], 10);
  return Number.isNaN(n) ? null : n;
}
